import React, { useEffect, useState } from 'react';
import { LtiVersion } from '@ublend-npm/aula-schema';

import {
  HANDIN_PROVIDER,
  TURNITIN_PROVIDER,
} from '@core/assignments/constants';
import { validateImageUrl } from '@core/utils/validateImageUrl';
import { Tool } from '../../utils/utils';
import { Box, Avatar, TextField, Icons, Radio } from '@ublend-npm/aulaui-core';

const DESCRIPTION = {
  [HANDIN_PROVIDER]: 'Recommended for text or multimedia submissions',
  [TURNITIN_PROVIDER]: 'Recommended when plagiarism checking is required',
};

type ToolSelectorProps = {
  styles?: any;
  currentTool: Tool;
  tools: Tool[];
  onToolChange: (tool: Tool) => void;
  onResourceLinkTitleChange: (value: string) => void;
  resourceLinkTitle: string;
};
export type FailedImagesType = {
  [key: string]: boolean;
};
const ToolSelector = ({
  styles,
  currentTool,
  tools,
  onToolChange,
  onResourceLinkTitleChange,
  resourceLinkTitle,
}: ToolSelectorProps) => {
  const [failedImages, setFailedImages] = useState<FailedImagesType>({});

  useEffect(() => {
    (async () => {
      const newFailedImages = {};

      const responses = await Promise.all(
        tools?.map((tool) => {
          if (tool.logo) {
            return validateImageUrl(tool?.logo);
          }
          return new Promise((resolve) => resolve(true));
        })
      );

      responses.forEach((resp, index) => {
        newFailedImages[tools[index]?.key] = !resp;
      });

      setFailedImages(newFailedImages);
    })();
  }, [tools]);

  const getToolAccessibleName = (tool: Tool) =>
    `${tool.name}${DESCRIPTION[tool.key] ? `, ${DESCRIPTION[tool.key]}` : ''}`;

  const renderAssignmentLabel = (tool: Tool) => {
    const imageIcon =
      tool?.logo && !failedImages[tool?.key] ? (
        <Avatar
          sx={styles.toolAvatar}
          src={tool.logo}
          fallbackText={tool?.name}
          altText=""
        />
      ) : (
        <Icons.ExtensionOutlined sx={styles.extensionIcon} aria-hidden />
      );

    return (
      <>
        <Box aria-hidden="true" sx={styles.toolListItem}>
          {imageIcon}
          <Box sx={styles.toolDetailsContainer}>
            <Box sx={styles.toolTitle}>{tool?.name}</Box>
            {DESCRIPTION[tool?.key] ? (
              <Box sx={styles.toolDescription}>{DESCRIPTION[tool?.key]}</Box>
            ) : null}
          </Box>
        </Box>
        <Box component="span" sx={styles.visuallyHidden}>
          {getToolAccessibleName(tool)}
        </Box>
      </>
    );
  };

  return (
    <Box sx={styles.toolsListWrapper}>
      {tools.map((tool) => {
        const selected = tool?.key === currentTool?.key;

        const showTitleField =
          selected &&
          tool?.ltiVersion === LtiVersion.ONE_POINT_THREE &&
          tool?.isDeepLinking === false &&
          !tool?.isCoupled;

        return (
          <Box key={tool.name}>
            <Radio
              label={renderAssignmentLabel(tool)}
              handleChange={() => onToolChange(tool)}
              isSelected={selected}
              value={tool.name}
              sx={styles.radio}
            />
            {showTitleField ? (
              <Box sx={styles.titleField}>
                <TextField
                  label="Assignment title"
                  kind="text"
                  placeholder="Assignment title"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onResourceLinkTitleChange(e.target.value)
                  }
                  value={resourceLinkTitle}
                />
              </Box>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
};

export default ToolSelector;
